/**
 * Single source of truth for Flavrly's social identity providers.
 *
 * Two lists, and the difference between them is load-bearing:
 *
 * - `ALL_SOCIAL_PROVIDERS` is every provider the auth service can handle. A
 *   provider only leaves this list if the backend support is removed.
 * - `ENABLED_SOCIAL_PROVIDERS` is what we currently OFFER: which sign-in
 *   buttons render, and which "Link account" actions are available.
 *
 * ENABLED governs whether we OFFER a provider. It must NOT govern whether a
 * provider is DISPLAYED. An account that already linked Apple has to keep
 * seeing that link -- and be able to unlink it -- even while Apple is disabled
 * for new sign-ins. So Connected Accounts iterates `ALL_SOCIAL_PROVIDERS` (plus
 * whatever the server reports as linked) and consults
 * `isSocialProviderEnabled` only to decide whether the Link action is offered.
 *
 * Turning a provider back on is a one-line edit to `ENABLED_SOCIAL_PROVIDERS`.
 */

import type { SocialProvider } from './sign-in-social.types';

/** Ionicons glyph names for the provider marks. */
export type SocialProviderIconName = 'logo-google' | 'logo-apple';

export interface SocialProviderMeta {
  readonly provider: SocialProvider;
  /** Bare provider name, for settings rows: "Google". */
  readonly name: string;
  /** Sign-in call to action, for auth screens: "Continue with Google". */
  readonly signInLabel: string;
  readonly icon: SocialProviderIconName;
}

/** Every provider Flavrly's auth service is able to support. */
export const ALL_SOCIAL_PROVIDERS: readonly SocialProvider[] = ['google', 'apple'];

/**
 * Providers currently offered for new sign-in and for linking.
 *
 * Apple is intentionally omitted: it is supported end to end but not offered
 * yet. Add `'apple'` here to turn it back on everywhere at once.
 */
export const ENABLED_SOCIAL_PROVIDERS: readonly SocialProvider[] = ['google'];

/** Display metadata shared by the sign-in buttons and the settings rows. */
export const SOCIAL_PROVIDER_META: Readonly<Record<SocialProvider, SocialProviderMeta>> = {
  google: {
    provider: 'google',
    name: 'Google',
    signInLabel: 'Continue with Google',
    icon: 'logo-google',
  },
  apple: {
    provider: 'apple',
    name: 'Apple',
    signInLabel: 'Continue with Apple',
    icon: 'logo-apple',
  },
};

/**
 * Whether we currently OFFER this provider (sign-in button, Link action).
 * Never use this to decide whether an already-linked provider is shown.
 */
export function isSocialProviderEnabled(provider: SocialProvider): boolean {
  return ENABLED_SOCIAL_PROVIDERS.includes(provider);
}
